import * as React from 'react';
import styled from 'styled-components';
import { observer } from 'mobx-react';
import { classes } from '../../utils/Classes';
import { IVtoShadeInfoLeft, VtoShadeInfoLeftContainer } from './VtoShadeInfoLeftContainer';
import { VtoShadeInfoRightContainer } from './VtoShadeInfoRightContainer';
import { VtoShadeCtaContainer } from './VtoShadeCtaContainer';
import {
    IProduct,
    IProductImage,
    IShade,
    IShadePicker,
    ISku,
    IVtoFeature
} from '../../interfaces/IVto';
import { configConsumer } from '@estee/elc-service';
import { hasSaveMatch } from '~utils/VtoFeatures';

interface IVtoShadeInfoContainer {
    isPairShadesTabActive: boolean;
    isAllShadesTabActive: boolean;
    pairProductData: {
        pairProduct?: IProduct;
        pairProductUrl: string;
        pairProductLargeImages: IProductImage[];
        selectedPairShade?: IShade;
        selectedPairSizeValue: string;
        selectedPairSku?: ISku;
    };
    saveMatches(): void;
    removeMatches(): void;
    isMatchSaved: boolean;
    isSaveInProgress: boolean;
    isVtoPriceHidden: boolean;
    config: IVtoShadeInfoConfig;
    productShadePickerProps: IShadePicker;
    togglePerfectShades(isEnabled: boolean): void;
    setActiveTab(activeTab: string): void;
    largeImages: IProductImage[];
    selectedAutoReplenishOption: number;
}

interface IVtoShadeInfoConfig {
    vtoFeature: IVtoFeature;
    hideProductPricePerUnit: boolean;
}

const Wrapper = styled.div`
    overflow: hidden;
`;

const shadeInfoContainerClassNames = classes('vto-shade-info-container');

const hasProductSize = (vtoFeature: IVtoFeature) => {
    return vtoFeature && vtoFeature.foundation && vtoFeature.foundation.hasProductSize;
};

const hasPairProduct = (vtoFeature: IVtoFeature, isPairShadesTabActive: boolean) => {
    const vtoFoundationFeature = vtoFeature && vtoFeature.foundation;

    return !!(vtoFoundationFeature && vtoFoundationFeature.hasLipVto && isPairShadesTabActive);
};

export const VtoShadeInfoContainer = configConsumer(['vtoFeature', 'hideProductPricePerUnit'])(
    observer(
        ({
            config,
            largeImages,
            togglePerfectShades,
            setActiveTab,
            selectedAutoReplenishOption,
            productShadePickerProps: { selectedSku, isPerfectShadeEnabled },
            isPairShadesTabActive,
            isAllShadesTabActive,
            pairProductData,
            saveMatches,
            removeMatches,
            isMatchSaved,
            isSaveInProgress,
            isVtoPriceHidden
        }: IVtoShadeInfoContainer) => {
            const {
                pairProduct,
                pairProductUrl,
                pairProductLargeImages,
                selectedPairShade,
                selectedPairSizeValue,
                selectedPairSku
            } = pairProductData;
            const { vtoFeature, hideProductPricePerUnit } = config;
            const isSaveMatchEnabled = hasSaveMatch(vtoFeature);
            const {
                parentProduct: { displayName, subHeader },
                selectedShade,
                selectedSize
            } = selectedSku;
            const selectedSizeValue = hasProductSize(vtoFeature) ? selectedSize.value : '';
            const showPairProduct = hasPairProduct(vtoFeature, isPairShadesTabActive);

            let shadeInfoLeftProps: IVtoShadeInfoLeft = {
                displayName,
                subHeader,
                selectedShade,
                selectedSizeValue,
                selectedSku
            };

            if (showPairProduct && selectedPairShade && selectedPairSku) {
                shadeInfoLeftProps = {
                    displayName: pairProduct ? pairProduct.displayName : '',
                    subHeader: pairProduct && pairProduct.subHeader,
                    selectedShade: selectedPairShade,
                    selectedSizeValue: selectedPairSizeValue,
                    selectedSku: selectedPairSku
                };
            }

            return (
                <Wrapper className={shadeInfoContainerClassNames}>
                    <VtoShadeInfoLeftContainer
                        {...shadeInfoLeftProps}
                        hideProductPricePerUnit={hideProductPricePerUnit}
                        hideProductPrice={isVtoPriceHidden}
                    />
                    <VtoShadeInfoRightContainer
                        isPerfectShadeEnabled={isPerfectShadeEnabled}
                        togglePerfectShades={togglePerfectShades}
                        setActiveTab={setActiveTab}
                        isPairShadesTabActive={isPairShadesTabActive}
                        isAllShadesTabActive={isAllShadesTabActive}
                        pairProductUrl={pairProductUrl}
                        saveMatches={saveMatches}
                        removeMatches={removeMatches}
                        isMatchSaved={isMatchSaved}
                        isSaveInProgress={isSaveInProgress}
                    />
                    <VtoShadeCtaContainer
                        config={config}
                        selectedSku={selectedSku}
                        largeImages={largeImages}
                        selectedAutoReplenishOption={selectedAutoReplenishOption}
                        isPairShadesTabActive={isPairShadesTabActive}
                        pairProductLargeImages={pairProductLargeImages}
                        selectedPairSku={selectedPairSku}
                        isSaveMatchEnabled={isSaveMatchEnabled}
                        isPerfectShadeEnabled={isPerfectShadeEnabled}
                    />
                </Wrapper>
            );
        }
    )
);
